import 'quill-mention/autoregister'
import 'quill/dist/quill.snow.css'

import { At, Attachment, Close, Link, Send } from '@carbon/icons-react'
import { DDSButton, DDSCheckbox, DDSTypography } from 'den-design-system'
import {
  MediaFile,
  RichTextEditorProps,
  RichTextPayload,
  User
} from './quill.types'
import Quill, { Delta } from 'quill'
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  convertStorage,
  parseFileInfoFromUrl
} from '../../../utils/IncidentUtil'

import { Button } from '../../New/Button/Button'
import CustomLinkEditor from './CustomLinkEditor'
import { FaLessThanEqual } from 'react-icons/fa'
import FilePreviewCard from '../../../pages/IncidentPage/FileTypes'
import { FileUploader } from 'react-drag-drop-files'
import LinkCard from './LinkCard'
import MediaPreviewGrid from './MediaPreviewGrid'
import MentionDropdown from './MentionDropdown'
import ReactDOMServer from 'react-dom/server'
import RegexConstants from '../../../constants/RegexConstants'
import SvgPreview from './PreviewSVG'
import ToastNotification from '../DDS/Toast/Toast'
import palette from '../../../global/pallete'
import pallete from '../../../global/newPallete'
import { useStoreContext } from '../../../store/StoreContext'

type RangeStatic = {
  index: number
  length: number
}

// URL detection regex pattern
const URL_PATTERN = RegexConstants.URL_REGEX

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  variant = 'create',
  placeholder = '',
  commentId,
  content = new Delta(),
  disabled = false,
  onSend,
  loading,
  payload,
  setPayload,
  isPublic = false,
  setIsPublic,
  assignMemberOptions,
  onCancel,
  PrimaryButtonText,
  primaryButtonOnClick,
  attachmentFile,
  setFilesToRemove,
  payloadUpdate,
  setPayloadUpdate,
  changeEdit,
  setChangeEdit,
  message,
  setActivityMessages
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const placeholderRef = useRef(placeholder)
  const quillRef = useRef<Quill | null>(null)
  const [atTriggerIndex, setAtTriggerIndex] = useState<number | null>(null)
  const [linkCardPosition, setLinkCardPosition] = useState({ top: 0, left: 0 })
  const contentRef = useRef(content)
  const containerRef = useRef<HTMLDivElement>(null)
  const disabledRef = useRef(disabled)
  const [lastSelection, setLastSelection] = useState<RangeStatic | null>(null)

  const [showLinkEditor, setShowLinkEditor] = useState(false)
  const [linkEditorData, setLinkEditorData] = useState({
    top: 0,
    left: 0,
    text: '',
    url: ''
  })
  const isSubmittingRef = useRef(false)

  const store = useStoreContext()
  const [mentionDropdown, setMentionDropdown] = useState<{
    show: boolean
    options: any[]
    top: number
    left: number
    upward: boolean
  }>({ show: false, options: [], top: 0, left: 0, upward: false })

  const [isLinkCardOpen, setIsLinkCardOpen] = useState(false)
  const [linkSelection, setLinkSelection] = useState<any>(null)

  const updateCommentsMentionsName = (
    ops: Delta,
    assignMemberOptions: User[]
  ) => {
    return new Delta(
      ops.ops.map((op) => {
        if (typeof op.insert === 'object' && op.insert.mention) {
          const mention: any = op.insert.mention
          const updatedMember = assignMemberOptions.find(
            (member) => member.id === mention.id
          )

          if (updatedMember) {
            return {
              ...op,
              insert: {
                mention: {
                  id: updatedMember.userId,
                  userId: updatedMember.userId,
                  firstName: updatedMember.firstName,
                  lastName: updatedMember.lastName,
                  value: updatedMember.name,
                  avatar: updatedMember.imgUrl,
                  denotationChar: mention.denotationChar || '@'
                }
              }
            }
          }
        }

        return op
      })
    )
  }

  useLayoutEffect(() => {
    placeholderRef.current = placeholder
    contentRef.current = updateCommentsMentionsName(
      content,
      assignMemberOptions
    )
    disabledRef.current = disabled
  }, [placeholder, content, assignMemberOptions, disabled])

  const handleOpenLinkCard = () => {
    if (!quillRef.current) return

    const linkButton = document.querySelector('.ql-link')
    if (linkButton) {
      const rect = linkButton.getBoundingClientRect()
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft

      setLinkCardPosition({
        top: rect.bottom + scrollTop,
        left: rect.left + scrollLeft
      })
    }

    const selection = quillRef.current.getSelection()
    if (selection) {
      setLinkSelection(selection)
      const { text, url } = getCurrentLink()
      setIsLinkCardOpen(true)
    }
  }

  const getCurrentLink = () => {
    const quill = quillRef.current
    if (!quill) return { text: '', url: '' }

    const selection = quill.getSelection()
    if (!selection) return { text: '', url: '' }

    const text = quill.getText(selection.index, selection.length)
    const format = quill.getFormat(selection)

    return {
      text,
      url: typeof format.link === 'string' ? format.link : ''
    }
  }

  const handleApplyLink = (text: string, url: string) => {
    const quill = quillRef.current
    if (!quill || !linkSelection) return

    quill.deleteText(linkSelection.index, linkSelection.length)
    quill.insertText(linkSelection.index, text, { link: url })
    quill.setSelection(linkSelection.index + text.length, 0)

    setIsLinkCardOpen(false)
  }

  const convertUrlsToLinks = (quill: Quill, text: string) => {
    const selection = quill.getSelection()
    if (!selection) return

    const urls = text.match(URL_PATTERN)
    if (!urls) return

    quill.deleteText(selection.index, text.length)

    let currentIndex = selection.index
    let lastIndex = 0

    text.replace(URL_PATTERN, (url, ...args) => {
      const matchIndex = args[args.length - 2]

      const beforeText = text.substring(lastIndex, matchIndex)
      if (beforeText) {
        quill.insertText(currentIndex, beforeText)
        currentIndex += beforeText.length
      }

      quill.insertText(currentIndex, url, { link: url })
      currentIndex += url.length

      lastIndex = matchIndex + url.length

      return url
    })

    const remainingText = text.substring(lastIndex)
    if (remainingText) {
      quill.insertText(currentIndex, remainingText)
      currentIndex += remainingText.length
    }

    quill.setSelection(currentIndex, 0)
  }
  const fileUploaderWrapperRef = useRef<HTMLDivElement>(null)

  const triggerFileUploader = () => {
    const input = fileUploaderWrapperRef.current?.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement | null

    if (input) {
      input.click()
    } else {
      console.error('File input not found inside FileUploader')
    }
  }

  const payloadRef = useRef(payload)

  useEffect(() => {
    payloadRef.current = payload
  }, [payload])

  const isPublicRef = useRef(false)

  useEffect(() => {
    isPublicRef.current = isPublic
  }, [isPublic])

  const mediaFilesExist = useMemo(
    () => payload?.mediaFiles?.length > 0,
    [payload?.mediaFiles]
  )

  const matchedAttachedFiles = useMemo(() => {
    const matched = attachmentFile?.find(
      (item: { id: string }) => item.id === commentId
    )
    return matched?.attachedFiles ?? []
  }, [attachmentFile, commentId])

  const isEditorEmpty =
    !mediaFilesExist &&
    (quillRef?.current?.getContents()?.ops ?? []).every((op) => {
      if (typeof op.insert === 'string') {
        return op.insert.trim() === '' || op.insert === '\n'
      }

      if (typeof op.insert === 'object' && op.insert !== null) {
        return false
      }

      return false
    })

  useEffect(() => {
    if (!containerRef.current) return
    const container = containerRef.current
    const editorContainer = container.appendChild(
      container.ownerDocument.createElement('div')
    )

    const icons: any = Quill.import('ui/icons')
    icons['fileUpload'] = ReactDOMServer.renderToString(
      <Attachment size={16} />
    )
    icons['link'] = ReactDOMServer.renderToString(<Link size={16} />)
    icons['mentionSomeone'] = ReactDOMServer.renderToString(<At size={16} />)

    const toolbarOptions = [
      ['fileUpload'],
      ['bold', 'strike', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }, 'link', 'mentionSomeone']
    ]

    const quill = new Quill(editorContainer, {
      theme: 'snow',
      placeholder: placeholderRef.current,
      modules: {
        mention: {
          mentionDenotationChars: ['@'],
          blotName: 'mention',
          showDenotationChar: true,
          source: function (
            searchTerm: any,
            renderList: any,
            mentionChar: any
          ) {
            const quill = quillRef.current
            if (!quill || mentionChar !== '@') return

            const range = quill.getSelection()
            if (!range) return

            const textBeforeCursor = quill.getText(
              Math.max(0, range.index - 1),
              1
            )
            const twoCharsBeforeCursor = quill.getText(
              Math.max(0, range.index - 2),
              2
            )

            if (twoCharsBeforeCursor === '@@') {
              setMentionDropdown((prev) => ({ ...prev, show: false }))
              return
            }

            if (textBeforeCursor === '@' && searchTerm.trim().length === 0) {
              setAtTriggerIndex(range.index - 1)
            }

            if (textBeforeCursor !== '@' && searchTerm.trim().length === 0)
              return

            const values = assignMemberOptions.map((member: User) => ({
              id: member.userId,
              userId: member.userId,
              value: member.name,
              firstName: member.firstName,
              lastName: member.lastName,
              avatar: member.imgUrl,
              denotationChar: mentionChar
            }))

            const matches =
              searchTerm.length > 0
                ? values.filter((member) =>
                    member.value
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                  )
                : values

            if (matches.length === 0) {
              setMentionDropdown((prev) => ({ ...prev, show: false }))
              return
            }

            const bounds: any = quill.getBounds(range.index)
            const containerRect = container.getBoundingClientRect()

            setMentionDropdown({
              show: true,
              options: matches,
              top: bounds.top + containerRect.top,
              left: bounds.left + containerRect.left,
              upward: true
            })
          },
          renderItem: () => document.createElement('div'),
          onSelect: () => {
            //
          }
        },
        toolbar: {
          container: toolbarOptions,
          handlers: {
            fileUpload: () => triggerFileUploader(),
            link: handleOpenLinkCard,
            mentionSomeone: () => {
              const quill = quillRef.current
              if (!quill) return

              const range = quill.getSelection()
              if (!range) return

              const charBefore = quill.getText(range.index - 1, 1)

              if (mentionDropdown.show) {
                setMentionDropdown((prev) => ({ ...prev, show: false }))
                return
              }

              if (charBefore === '@') {
                // Re-open dropdown (3rd click)
                setAtTriggerIndex(range.index - 1)
                quill.setSelection(range.index)

                const mentionModule = quill.getModule('mention') as {
                  source: (
                    searchTerm: string,
                    renderList: any,
                    mentionChar: string
                  ) => void
                }

                if (mentionModule?.source) {
                  mentionModule.source(
                    '',
                    () => {
                      //
                    },
                    '@'
                  )
                }

                return
              }

              quill.insertText(range.index, '@', 'user')
              quill.setSelection(range.index + 1)
              setAtTriggerIndex(range.index)
              quill.focus()

              setTimeout(() => {
                const mentionModule = quill.getModule('mention') as {
                  source: (
                    searchTerm: string,
                    renderList: any,
                    mentionChar: string
                  ) => void
                }

                if (mentionModule?.source) {
                  mentionModule.source(
                    '',
                    () => {
                      //
                    },
                    '@'
                  )
                }
              }, 0)
            }
          }
        }
      }
    })

    const isDeltaFormat =
      typeof content === 'object' && content?.ops !== undefined

    if (isDeltaFormat) {
      quill.setContents(content as Delta)
    } else if (typeof content === 'string') {
      quill.setText(content)
    }

    // ✅ Prevent drag-and-drop of files/images
    const blockDragAndDrop = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }
    quill.root.addEventListener('dragover', blockDragAndDrop)
    quill.root.addEventListener('drop', blockDragAndDrop)

    // ✅ Prevent drop on entire document or editor container
    document.addEventListener('dragover', blockDragAndDrop)
    document.addEventListener('drop', blockDragAndDrop)

    // Store reference & setup
    quillRef.current = quill
    quill.focus()
    quill.setContents(contentRef.current)
    setPayload((prev: any) => ({ ...prev, content: quill.getContents() }))

    // Selection change
    quill.on('selection-change', (range, oldRange, source) => {
      const editorRoot = quill.root
      const linkEditorEl = document.querySelector('#custom-link-editor') // Add ID to your CustomLinkEditor root

      const activeEl = document.activeElement

      if (
        !range &&
        (!linkEditorEl || !linkEditorEl.contains(activeEl)) &&
        !editorRoot.contains(activeEl)
      ) {
        setShowLinkEditor(false)
        return
      }

      if (!range || range.length === 0) {
        return
      }

      const formats = quill.getFormat(range)

      if (formats.link) {
        const bounds: any = quill.getBounds(range)
        const selectedText = quill.getText(range.index, range.length)
        const link = typeof formats.link === 'string' ? formats.link : ''

        setLinkSelection(range)

        setLinkEditorData({
          top: bounds.top + 35,
          left: bounds.left,
          text: selectedText,
          url: link
        })

        setShowLinkEditor(true)
      }
    })

    quillRef.current = quill
    quill.focus()

    quill.setContents(contentRef.current)
    setPayload((prev: RichTextPayload) => ({
      ...prev,
      content: quill.getContents()
    }))

    quill.on(Quill.events.SELECTION_CHANGE, (range) => {
      if (range) setLastSelection(range)
    })

    quill.on(Quill.events.TEXT_CHANGE, (delta, oldDelta, source) => {
      const text = quill.getText()
      const maxLength = 3000

      if (text.length > maxLength) {
        quill.deleteText(maxLength, text.length)

        return
      }

      const range = quill.getSelection()
      if (range) setLastSelection(range)

      if (!text.includes('@')) {
        setMentionDropdown((prev) => ({ ...prev, show: false }))
      }

      setPayload((prev: RichTextPayload) => ({
        ...prev,
        content: quill.getContents()
      }))
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()

        if (isSubmittingRef.current) return

        const currentContents = quill.getContents()

        const isEditorEmpty = (currentContents.ops ?? []).every((op) => {
          return typeof op.insert === 'string'
            ? op.insert.trim() === '' || op.insert === '\n'
            : false
        })

        const hasMediaFiles = (payloadRef.current?.mediaFiles ?? []).length > 0

        if (isEditorEmpty && !hasMediaFiles) {
          return
        }

        const contentPayload = {
          ...payloadRef.current,
          content: currentContents,
          isPublic: isPublicRef.current
        }

        const contentPayloadUpdate = {
          ...payloadRef.current,
          content: currentContents
        }

        isSubmittingRef.current = true

        if (variant === 'create' && onSend) {
          onSend(contentPayload)
        }

        if (variant === 'update' && primaryButtonOnClick) {
          primaryButtonOnClick(contentPayloadUpdate)
        }

        setTimeout(() => {
          isSubmittingRef.current = false
        }, 3000)
      }

      if (mentionDropdown.show) {
        if (event.key === '@') {
          event.preventDefault()
          return
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault()
          return
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          setMentionDropdown((prev) => ({ ...prev, show: false }))
          return
        }
      }
    }

    quill.root.addEventListener('keydown', handleKeyDown, true)

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        return false
      }
    }

    quill.root.addEventListener('keydown', handleKeyDown, true)
    quill.root.addEventListener('keypress', handleKeyPress, true)

    editorContainer.addEventListener('paste', (event) => {
      setTimeout(() => {
        const selection = quill.getSelection()
        if (selection) {
          const clipboardData =
            event.clipboardData || (window as any).clipboardData
          if (clipboardData && clipboardData.getData) {
            const pastedText = clipboardData.getData('text/plain')

            if (pastedText.match(URL_PATTERN)) {
              event.preventDefault()
              convertUrlsToLinks(quill, pastedText)
            }
          }
        }
      }, 0)
    })

    const handleOutsideClick = () => {
      setMentionDropdown((prev) => ({ ...prev, show: false }))
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleOutsideClick()
    }

    document.addEventListener('click', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)

    return () => {
      quill.off(Quill.events.TEXT_CHANGE)
      quill.root.removeEventListener('dragover', blockDragAndDrop)
      quill.root.removeEventListener('drop', blockDragAndDrop)
      document.removeEventListener('dragover', blockDragAndDrop)
      document.removeEventListener('drop', blockDragAndDrop)
      quill.root.removeEventListener('keydown', handleKeyDown, true)
      quill.root.removeEventListener('keypress', handleKeyPress, true)
      document.removeEventListener('click', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
      container.innerHTML = ''
      quillRef.current = null
    }
  }, [assignMemberOptions, variant, payloadRef])

  const handleFileUpload = async (files: File[] | FileList) => {
    try {
      if (!files || files.length === 0) {
        throw new Error('No files provided for upload.')
      }

      const newFilesArray = Array.from(files)

      const newFiles: MediaFile[] = newFilesArray.map((file) => ({
        id: `${file.name}-${crypto.randomUUID()}`,
        file,
        fileUrl: URL.createObjectURL(file),
        isNew: true
      }))

      const currentSizeMB = payload.mediaFiles.reduce(
        (sum, item) => sum + item.file.size / (1024 * 1024),
        0
      )

      const newFilesSizeMB = newFiles.reduce(
        (sum, item) => sum + item.file.size / (1024 * 1024),
        0
      )

      const totalSizeMB = currentSizeMB + newFilesSizeMB

      if (totalSizeMB > 10) {
        ToastNotification({
          type: 'error',
          message:
            'Total file size exceeds the 10MB limit. Please remove some files or upload smaller ones.'
        })
        return
      }

      setPayload((prev: any) => ({
        ...prev,
        mediaFiles: [...prev.mediaFiles, ...newFiles]
      }))
    } catch (error) {
      console.error('File upload failed:', error)
      ToastNotification({ type: 'error', message: 'File upload failed!' })
    }
  }

  const handleFileRemove = (fileUrlToRemove: string) => {
    setFilesToRemove((prev: string[]) => [...prev, fileUrlToRemove])
    setActivityMessages((prev: any[]) => {
      const updated = prev.map((item) => {
        const newFiles = item.attachedFiles.filter(
          (file: string) => file !== fileUrlToRemove
        )

        if (newFiles.length === 0) {
          console.log(`All attached files removed for comment ID: ${item.id}`)
        }

        return {
          ...item,
          attachedFiles: newFiles
        }
      })

      return updated
    })
  }

  const handleFileRemoveCreate = (id: string) => {
    setPayload((prev: any) => ({
      ...prev,
      mediaFiles: prev.mediaFiles.filter((file: any) => file.id !== id)
    }))
  }

  return (
    <div
      className={`flex flex-col w-full ${mediaFilesExist ? 'max-h-[1000px] ' : 'max-h-[1000px]'}  relative    top-[0px] ${
        loading ? 'pointer-events-none cursor-not-allowed' : ''
      }  `}
    >
      <div
        className={`flex flex-col w-full ${mediaFilesExist ? 'max-h-[1000px]' : 'max-h-[1000px]'} `}
      >
        <div className='flex flex-col border rounded-md overflow-hidden focus-within:border-slate focus-within:shadow-sm transition bg-white'>
          <div>
            <div className='hidden' ref={fileUploaderWrapperRef}>
              <FileUploader
                multiple
                ref={fileUploaderWrapperRef}
                handleChange={handleFileUpload}
                name='file'
                disabled={disabled}
              />
            </div>

            <div>
              <div
                id={commentId}
                ref={containerRef}
                className={` ${mediaFilesExist ? 'min-h-[105px] max-h-[200px]' : 'min-h-[105px] max-h-[200px]'} overflow-y-auto ql-custom`}
              />
            </div>
          </div>
          {variant === 'update' && matchedAttachedFiles.length > 0 && (
            <>
              <div>
                <div className='flex flex-wrap  rounded-lg w-full'>
                  <MediaPreviewGrid
                    attachedFiles={matchedAttachedFiles}
                    variant='close'
                    handleFileRemove={handleFileRemove}
                  />
                </div>

                <div className='flex flex-wrap gap-[12px] pt-[12px]  pl-[12px]'>
                  {matchedAttachedFiles.map((media: any, index: number) => {
                    const isHeic =
                      media.includes('heic') ||
                      media.includes('heif') ||
                      media.endsWith('.heic') ||
                      media.endsWith('.heif')
                    const isImage = media.includes('image/') && !isHeic
                    const isVideo = media.includes('video')
                    const none = media.includes('blob')
                    const fileInfo = parseFileInfoFromUrl(media)

                    return (
                      <React.Fragment key={media.fileUrl || media}>
                        {!isImage && !isVideo && !none && (
                          <FilePreviewCard
                            file={{
                              name: fileInfo.name,
                              type: fileInfo.type,
                              url: media,
                              size: convertStorage(fileInfo.size)
                            }}
                            uploadType='create'
                            onClose={() => handleFileRemove(media)}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                </div>
              </div>
            </>
          )}

          <div>
            {mediaFilesExist && variant === 'create' && (
              <>
                <div className='flex flex-wrap  rounded-lg w-full'>
                  {payload.mediaFiles.map((media: any) => {
                    const isHeic =
                      media.file?.type.includes('heic') ||
                      media.file?.type.includes('heif') ||
                      media.file?.type.endsWith('.heic') ||
                      media.file?.type.endsWith('.heif')
                    const isImage =
                      media.file?.type.includes('image/') && !isHeic
                    const isVideo = media.file?.type.includes('video')

                    return (
                      <div key={media.id} className='group'>
                        {isVideo && (
                          <div className='w-[100px] h-[80px] pl-[8px] flex justify-center relative'>
                            <video
                              src={media.fileUrl}
                              className='object-cover rounded-md w-full h-full'
                              controls
                              muted
                              playsInline
                            />
                            <button
                              className='absolute -top-[3px] -right-[3px] w-[24px] h-[24px] rounded-full  text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px] '
                              onClick={() => handleFileRemoveCreate(media.id)}
                              style={{
                                borderColor: pallete.colors.borderWhite,
                                backgroundColor: pallete.colors.lightWhite
                              }}
                            >
                              <Close size={12} />
                            </button>
                          </div>
                        )}

                        {isImage && (
                          <div className='w-[100px] h-[80px] pl-[8px] flex justify-center relative'>
                            <img
                              src={media.fileUrl}
                              alt='media'
                              className='object-cover rounded-md w-full h-full'
                            />

                            <button
                              style={{
                                borderColor: pallete.colors.borderWhite,
                                backgroundColor: pallete.colors.lightWhite
                              }}
                              className='absolute -top-[3px] -right-[3px] w-[24px] h-[24px] rounded-full text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px]'
                              onClick={() => handleFileRemoveCreate(media.id)}
                            >
                              <Close size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>

                <div className='flex flex-wrap pl-[12px] pt-[12px]   gap-[12px]'>
                  {payload.mediaFiles.map((media: any) => {
                    const isHeic =
                      media.file?.type.includes('heic') ||
                      media.file?.type.includes('heif') ||
                      media.file?.type.endsWith('.heic') ||
                      media.file?.type.endsWith('.heif')
                    const isImage =
                      media.file?.type.includes('image/') && !isHeic
                    const isVideo = media.file?.type.includes('video')

                    return (
                      <React.Fragment key={media.id}>
                        {!isImage && !isVideo && (
                          <FilePreviewCard
                            file={{
                              name: media.file?.name || 'file',
                              type: media.file?.type || '',
                              url: media.fileUrl,
                              size: convertStorage(media.file.size)
                            }}
                            activity='activity'
                            uploadType='create'
                            onClose={() => handleFileRemoveCreate(media.id)}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                </div>
              </>
            )}
          </div>
          <div>
            {mediaFilesExist && variant === 'update' && changeEdit && (
              <>
                <div className='flex flex-wrap  rounded-lg w-full'>
                  {payload.mediaFiles.map((media: any) => {
                    const isHeic =
                      media.file?.type.includes('heic') ||
                      media.file?.type.includes('heif') ||
                      media.file?.type.endsWith('.heic') ||
                      media.file?.type.endsWith('.heif')
                    const isImage =
                      media.file?.type.includes('image/') && !isHeic
                    const isVideo = media.file?.type.includes('video')
                    const isSVG = media.file?.type.endsWith('.svg')

                    return (
                      <div key={media.id} className='group'>
                        {isVideo && (
                          <div className='w-[100px] h-[80px] pl-[8px]  flex justify-center relative'>
                            <video
                              src={media.fileUrl}
                              className='object-cover rounded-md w-full h-full'
                              controls
                              muted
                              playsInline
                            />
                            <button
                              style={{
                                borderColor: pallete.colors.borderWhite,
                                backgroundColor: pallete.colors.lightWhite
                              }}
                              className='absolute -top-[3px] -right-[3px] w-[24px] h-[24px] rounded-full text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px]'
                              onClick={() => handleFileRemoveCreate(media.id)}
                            >
                              <Close size={12} />
                            </button>
                          </div>
                        )}

                        {isImage && (
                          <div className='w-[100px] h-[80px] pl-[8px]  flex justify-center relative'>
                            {isSVG ? (
                              <SvgPreview url={media.fileUrl} />
                            ) : (
                              <img
                                src={media.fileUrl}
                                alt='media'
                                className='object-cover rounded-md w-full h-full'
                              />
                            )}

                            <button
                              className='absolute -top-[3px] -right-[3px] w-[24px] h-[24px] rounded-full  text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px]'
                              style={{
                                borderColor: pallete.colors.borderWhite,
                                backgroundColor: pallete.colors.lightWhite
                              }}
                              onClick={() => handleFileRemoveCreate(media.id)}
                            >
                              <Close size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>

                <div className='flex flex-wrap pl-[12px] pt-[12px]   gap-[12px]'>
                  {payload.mediaFiles.map((media: any) => {
                    const isHeic =
                      media.file?.type.includes('heic') ||
                      media.file?.type.includes('heif') ||
                      media.file?.type.endsWith('.heic') ||
                      media.file?.type.endsWith('.heif')
                    const isImage =
                      media.file?.type.includes('image/') && !isHeic
                    const isVideo = media.file?.type.includes('video')

                    return (
                      <React.Fragment key={media.id}>
                        {!isImage && !isVideo && (
                          <FilePreviewCard
                            file={{
                              name: media.file?.name || 'file',
                              type: media.file?.type || '',
                              url: media.fileUrl,
                              size: convertStorage(media.file.size)
                            }}
                            activity='activity'
                            uploadType='create'
                            onClose={() => handleFileRemoveCreate(media.id)}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                </div>
              </>
            )}
          </div>

          <div
            className={`px-10px py-10px flex items-center  ${mediaFilesExist ? '' : ''} bg-white`}
          >
            {variant === 'create' && (
              <div className='flex-grow px-[8px]'>
                <DDSCheckbox
                  label={
                    <DDSTypography.Paragraph
                      size='caption'
                      variant='medium'
                      color={palette.colors.black}
                    >
                      Make it visible for the user
                    </DDSTypography.Paragraph>
                  }
                  checked={isPublic}
                  disabled={store.userStore.getNoActiveSubscription()}
                  onChange={() => setIsPublic(!isPublic)}
                />
              </div>
            )}
            {variant === 'create' ? (
              <div className='flex items-center px-[10px] py-[2px]'>
                <DDSButton
                  type='filled'
                  size='small'
                  loading={loading}
                  startIcon={<Send color={palette.colors.white} />}
                  id='send-button'
                  disabled={
                    isEditorEmpty ||
                    loading ||
                    store.userStore.getNoActiveSubscription()
                  }
                  onClick={() => {
                    const contentPayload = {
                      ...payload,
                      isPublic: isPublic
                    }
                    if (!isEditorEmpty && onSend && !loading) {
                      onSend(contentPayload)
                    }
                  }}
                />
              </div>
            ) : (
              <div className='flex justify-end items-end w-full'>
                <div className='flex gap-[8px] px-[4px] py-[4px]'>
                  <Button
                    label='Cancel'
                    type='neutral'
                    size='small'
                    id='cancel-button'
                    onClick={() => {
                      if (onCancel && !loading) {
                        onCancel()
                      }
                    }}
                    disabled={loading}
                  />
                  <Button
                    label={PrimaryButtonText}
                    type='filled'
                    size='small'
                    id={PrimaryButtonText as string}
                    loading={loading}
                    onClick={() => {
                      if (primaryButtonOnClick) {
                        primaryButtonOnClick(payload)
                      }
                    }}
                    disabled={
                      loading || store.userStore.getNoActiveSubscription()
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <LinkCard
        isOpen={isLinkCardOpen}
        onClose={() => setIsLinkCardOpen(false)}
        onApply={handleApplyLink}
        initialText={getCurrentLink().text}
        initialUrl={getCurrentLink().url}
        variant={variant}
      />
      {mentionDropdown.show && (
        <MentionDropdown
          options={mentionDropdown.options}
          top={mentionDropdown.top}
          left={mentionDropdown.left}
          upward={mentionDropdown.upward}
          onSelect={(item) => {
            const quill = quillRef.current
            if (!quill || atTriggerIndex == null || !lastSelection) return

            const range = quill.getSelection()
            const deleteLength = lastSelection.index - atTriggerIndex

            // 🧹 Remove any extra "@" BEFORE the mention
            const charBefore = quill.getText(atTriggerIndex - 1, 1)
            if (charBefore === '@') {
              quill.deleteText(atTriggerIndex - 1, 1)
              setAtTriggerIndex(atTriggerIndex - 1)
            }

            // 🧹 Remove the "@typedText"
            quill.deleteText(atTriggerIndex, deleteLength)

            // ✅ Now check for trailing rogue @ AFTER the cursor
            const charAfter = quill.getText(atTriggerIndex, 1)
            if (charAfter === '@') {
              quill.deleteText(atTriggerIndex, 1)
            }

            // ✅ Insert mention
            quill.insertEmbed(atTriggerIndex, 'mention', {
              id: item.userId,
              userId: item.userId,
              value: item.value,
              firstName: item.firstName,
              lastName: item.lastName,
              avatar: item.avatar,
              denotationChar: '@'
            })

            // ✅ Insert space after mention
            quill.insertText(atTriggerIndex + 1, ' ')
            quill.setSelection(atTriggerIndex + 2)

            // ✅ Update content
            setPayload((prev: RichTextPayload) => ({
              ...prev,
              content: quill.getContents()
            }))

            setMentionDropdown((prev) => ({ ...prev, show: false }))
            setAtTriggerIndex(null)
          }}
        />
      )}
      {showLinkEditor && (
        <CustomLinkEditor
          variant={variant}
          initialText={linkEditorData.text}
          initialUrl={linkEditorData.url}
          position={{
            top: linkEditorData.top,
            left: linkEditorData.left
          }}
          onUpdate={(newText, newUrl) => {
            const quill = quillRef.current
            if (!quill || !linkSelection) {
              return
            }

            // Replace selected text with updated link
            quill.deleteText(linkSelection.index, linkSelection.length)
            quill.insertText(linkSelection.index, newText, { link: newUrl })
            quill.setSelection(linkSelection.index + newText.length, 0)

            setShowLinkEditor(false)
          }}
          onRemove={() => {
            const quill = quillRef.current
            if (!quill || !linkSelection) {
              return
            }

            quill.formatText(
              linkSelection.index,
              linkSelection.length,
              'link',
              false
            )

            setShowLinkEditor(false)
          }}
          onClose={() => {
            setShowLinkEditor(false)
          }}
        />
      )}
    </div>
  )
}

export default RichTextEditor
